import React, { useEffect, useRef, useState } from "react";
import throttle from "lodash/throttle";
import {
  EVENTS,
  PROMPT_TIMEOUT,
  ACTIVITY_LOG_INTERVAL,
  LOGOUT_TIMEOUT,
} from "./config";
import { Modal } from "../../shared/Modal/Modal";
import { Button } from "../../shared/Button/Button";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { logoutByIdle } from "./utilities";
import { useModalState } from "./hooks/useModalState";
import { authService } from "../../services/AuthService";
import { LOCAL_STORAGE_KEYS } from "../../consts/localStorage";
import styles from "./styles.module.scss";

export const InactivityModal = () => {
  const { tCommon: t } = useAppTranslation.Common();
  const modal = useModalState();

  const [lastActivity, setLastActivity] = useState(Date.now());
  const [modalOpenTime, setModalOpenTime] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEYS.idle_modalOpenTime || 0),
  );

  const throttledSave = useRef(
    throttle((timestamp) => {
      localStorage.setItem(LOCAL_STORAGE_KEYS.idle_lastActive, timestamp);
    }, ACTIVITY_LOG_INTERVAL),
  );

  const handleStay = () => {
    const now = Date.now();
    setLastActivity(now);
    setModalOpenTime(0);
    localStorage.setItem(LOCAL_STORAGE_KEYS.idle_lastActive, now);
    localStorage.setItem(LOCAL_STORAGE_KEYS.idle_modalOpenTime, 0);
    modal.close();
  };

  const manualLogout = () => {
    modal.close();
    logoutByIdle(false);
  };

  useEffect(() => {
    const handleActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      throttledSave?.current(now);
    };

    const handleStorage = (e) => {
      if (e.key === LOCAL_STORAGE_KEYS.idle_lastActive) {
        const otherTabActivity = Number(e.newValue);
        if (!isNaN(otherTabActivity)) {
          setLastActivity(otherTabActivity);
        }
      }

      if (e.key === LOCAL_STORAGE_KEYS.idle_modalOpenTime) {
        const modalTime = Number(e.newValue);
        if (!isNaN(modalTime)) {
          setModalOpenTime(modalTime);
        }
      }
    };

    /**
     * Activity events
     */
    EVENTS.forEach((event) => window.addEventListener(event, handleActivity));

    /**
     * Listen for localStorage changes from other tabs
     * This is necessary to synchronize activity across multiple tabs
     * especially when the user is active in one tab but not in another
     */
    window.addEventListener("storage", handleStorage);

    /**
     * Track the last activity time
     * @type {number}
     */
    const interval = setInterval(() => {
      if (!authService.isLoggedIn()) {
        return false;
      }

      const now = Date.now();

      if (now - lastActivity > PROMPT_TIMEOUT && !modal.isOpen()) {
        modal.open();
        localStorage.setItem(LOCAL_STORAGE_KEYS.idle_modalOpenTime, now);
        setModalOpenTime(now);
      } else if (now - modalOpenTime > LOGOUT_TIMEOUT && modal.isOpen()) {
        logoutByIdle(false);
      }
    }, 1000);

    /**
     * Clean up event listeners and interval on component unmount
     */
    return () => {
      EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, [lastActivity, modal.isOpen(), authService.isLoggedIn(), modalOpenTime]);

  return (
    <>
      {modal.isOpen() && (
        <Modal
          isOpen
          footerNoBorder
          onClose={modal.close}
          shouldCloseOnOverlayClick={false}
          header={<Modal.Title>{t("idle.title")}</Modal.Title>}
          footer={
            <div className={styles.footer}>
              <Button onClick={handleStay}>{t("idle.stay")}</Button>
              <Button variant="outlined" onClick={manualLogout}>
                {t("idle.logout")}
              </Button>
            </div>
          }
        >
          {t("idle.description")}
        </Modal>
      )}
    </>
  );
};
